/**
 * Merchant normalisation.
 *
 * Card and wallet descriptors are noisy: "TNG*KOPI KITA SS15 PJ", "GRAB*
 * A-6XKQ2", "SHELL 1234 JLN AMPANG KUALA LUMPUR". Normalising them gives three
 * things: readable history, working merchant suggestions, and a dedupe key that
 * survives the same purchase arriving from two different sources.
 *
 * The rules are deliberately conservative -- when in doubt the original text is
 * kept. Nothing here invents a merchant that was not in the input.
 */

/** Prefixes that acquirers and wallets bolt onto the front of a descriptor. */
const LEADING_NOISE = [
  'tng',
  'tngd',
  'tng ewallet',
  'grab',
  'grabpay',
  'shopeepay',
  'boost',
  'fpx',
  'duitnow',
  'duitnow qr',
  'qr pay',
  'qrpay',
  'qr',
  'pos',
  'ipay88',
  'billplz',
  'senangpay',
  'paypal',
  'sq',
  'sp',
  'pb',
  'mb2u',
  'mae',
  'debit purchase',
  'credit purchase',
  'purchase',
  'payment to',
  'payment',
  'pymt',
  'retail purchase',
];

/** Company suffixes that add nothing to a merchant's identity. */
const TRAILING_NOISE = [
  'sdn bhd',
  'sdn. bhd.',
  'sdn bhd.',
  'bhd',
  'berhad',
  'enterprise',
  'ent',
  'trading',
  'holdings',
  'pte ltd',
  'plt',
  'llp',
  'inc',
  'ltd',
  'co',
];

/** Malaysian place names that commonly tail a POS descriptor. */
const PLACE_NOISE = [
  'kuala lumpur',
  'wilayah persekutuan',
  'petaling jaya',
  'subang jaya',
  'shah alam',
  'cyberjaya',
  'putrajaya',
  'seri kembangan',
  'johor bahru',
  'george town',
  'kota kinabalu',
  'kuching',
  'penang',
  'selangor',
  'malaysia',
  'my',
  'mys',
];

const SMALL_WORDS = new Set(['and', 'of', 'the', 'de', 'da', 'dan', 'di', 'bin', 'binti']);

/** Words that should keep their all-caps form when title casing. */
const KEEP_UPPER = new Set([
  '7',
  '99',
  'kl',
  'klcc',
  'klia',
  'klia2',
  'tng',
  'mae',
  'lrt',
  'mrt',
  'ktm',
  'atm',
  'bhd',
  'ss15',
  'ss2',
  'usj',
  'pj',
  'jb',
  'tbs',
  'ioi',
  'mbb',
  'my',
  'ai',
  'it',
  'tv',
  'dvd',
  'bbq',
]);

const escapeRegExp = (value: string): string => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const byLengthDesc = (a: string, b: string): number => b.length - a.length;

// Longest first, so "duitnow qr" is consumed before the shorter "duitnow".
const LEADING_NOISE_ORDERED = [...LEADING_NOISE].sort(byLengthDesc);
const TRAILING_NOISE_ORDERED = [...TRAILING_NOISE, ...PLACE_NOISE].sort(byLengthDesc);

function stripEdgeNoise(value: string): string {
  let text = value;

  // Leading tokens, possibly repeated: "PB*TNG*KOPI KITA".
  for (let pass = 0; pass < 3; pass += 1) {
    const before = text;
    for (const prefix of LEADING_NOISE_ORDERED) {
      const pattern = new RegExp(`^${escapeRegExp(prefix)}(?:\\s*[*:\\-/]\\s*|\\s+)`, 'i');
      if (pattern.test(text)) {
        const stripped = text.replace(pattern, '').trim();
        // Never strip the whole thing away; a bare "TNG" stays "TNG".
        if (stripped.length >= 2) text = stripped;
      }
    }
    if (text === before) break;
  }

  // Trailing company forms and place names.
  for (let pass = 0; pass < 3; pass += 1) {
    const before = text;
    for (const suffix of TRAILING_NOISE_ORDERED) {
      const pattern = new RegExp(`[\\s,.-]+${escapeRegExp(suffix)}\\.?$`, 'i');
      const stripped = text.replace(pattern, '').trim();
      if (stripped.length >= 2) text = stripped;
    }
    if (text === before) break;
  }

  return text;
}

/**
 * A display-ready merchant name.
 * "TNG*KOPI KITA SS15 PJ  " -> "Kopi Kita SS15 PJ"
 */
export function normalizeMerchant(raw: string | null | undefined): string {
  if (!raw) return '';

  let text = String(raw)
    .normalize('NFKC')
    // eslint-disable-next-line no-control-regex
    .replace(/[\u0000-\u001f\u007f-\u009f]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  if (!text) return '';

  // Reference blobs that follow a separator: "GRAB* A-6XKQ2H9", "FOODPANDA #8812345".
  text = text.replace(/\s*[*#]\s*[A-Z0-9-]{6,}\s*$/i, '').trim();
  // Trailing terminal / store / batch numbers.
  text = text.replace(/\s+(?:no\.?|ref\.?|trx|txn|id)?\s*[#-]?\d{4,}\s*$/i, '').trim();
  text = stripEdgeNoise(text);
  // Collapse leftover separators.
  text = text
    .replace(/[*|]+/g, ' ')
    .replace(/\s*[-–—]\s*$/, '')
    .replace(/\s+/g, ' ')
    .trim();

  if (!text) return String(raw).trim().slice(0, 120);

  return titleCase(text).slice(0, 120);
}

function titleCase(value: string): string {
  const alreadyMixed = /[a-z]/.test(value) && /[A-Z]/.test(value);
  const words = value.split(' ');
  return words
    .map((word, index) => {
      const lower = word.toLowerCase();
      const bare = lower.replace(/[^a-z0-9]/g, '');
      if (KEEP_UPPER.has(bare)) return word.toUpperCase();
      // Leave names that a human already cased alone, e.g. "McDonald's", "IKEA".
      if (alreadyMixed && /^[A-Z][a-z]/.test(word)) return word;
      if (index > 0 && SMALL_WORDS.has(lower)) return lower;
      if (/^\d/.test(word)) return word;
      return lower.charAt(0).toUpperCase() + lower.slice(1);
    })
    .join(' ');
}

/**
 * The matching form: lowercase, alphanumerics and single spaces only.
 * Used for rules, suggestions and deduplication -- never shown to the user.
 */
export function merchantKey(raw: string | null | undefined): string {
  return normalizeMerchant(raw)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

/** Do two merchant strings refer to the same place, as far as we can tell? */
export function sameMerchant(a: string | null | undefined, b: string | null | undefined): boolean {
  const keyA = merchantKey(a);
  const keyB = merchantKey(b);
  if (!keyA || !keyB) return false;
  if (keyA === keyB) return true;
  // One being a whole-word prefix of the other covers "Kopi Kita" vs "Kopi Kita SS15".
  return keyA.startsWith(`${keyB} `) || keyB.startsWith(`${keyA} `);
}

export interface MerchantSuggestion {
  merchant: string;
  categoryId: string;
  /** How many past transactions back this suggestion. */
  count: number;
  lastUsedAt: string;
}

/**
 * Rank previously used merchants for the quick-entry field. Recency and
 * frequency both matter; an exact prefix match always wins.
 */
export function rankMerchantSuggestions(
  history: readonly MerchantSuggestion[],
  query: string,
  limit = 6,
): MerchantSuggestion[] {
  const needle = merchantKey(query);
  const scored = history
    .map((entry) => {
      const key = merchantKey(entry.merchant);
      let score = 0;
      if (!needle) {
        score = 1;
      } else if (key === needle) {
        score = 1000;
      } else if (key.startsWith(needle)) {
        score = 500;
      } else if (key.includes(needle)) {
        score = 200;
      } else {
        return null;
      }
      const ageDays = Math.max(
        0,
        (Date.now() - Date.parse(entry.lastUsedAt)) / 86_400_000,
      );
      const recency = Math.max(0, 60 - ageDays);
      return { entry, score: score + Math.min(entry.count, 50) * 2 + recency };
    })
    .filter((value): value is { entry: MerchantSuggestion; score: number } => value !== null);

  scored.sort((a, b) => b.score - a.score || a.entry.merchant.localeCompare(b.entry.merchant));
  return scored.slice(0, limit).map((item) => item.entry);
}
